import {
    booleanAttribute,
    Component,
    ContentChild,
    EventEmitter,
    forwardRef,
    Input,
    input,
    Output,
    Pipe,
    PipeTransform,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CeCheckboxHelperTextDirective, CeErrorMessageDirective } from './ce-checkbox.directives';
import { Icons } from '../ce-icon/ICONLIB';
import { v4 as uuid } from 'uuid';
import { CeIconComponent } from '../ce-icon/ce-icon.component';

// Event emitted by a CeCheckboxComponent when the checked state changes.
// Includes a reference to the source CeCheckboxComponent in case it is needed.
export class CeCheckboxChange {
    source!: CeCheckboxComponent;
    checked!: boolean;
}

/**
 * Turns HTML tags within label to strings, but allows <mark> tags as HTML, so partial string matches can be displayed.
 */
@Pipe({
    name: 'safeHtmlPipe',
    standalone: true,
})
export class SafeHtmlPipe implements PipeTransform {
    transform(label: string): string {
        return label
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('&lt;mark&gt;', '<mark>')
            .replaceAll('&lt;/mark&gt;', '</mark>');
    }
}

@Component({
    selector: 'ce-checkbox',
    imports: [CommonModule, FormsModule, SafeHtmlPipe, CeIconComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef((): typeof CeCheckboxComponent => CeCheckboxComponent),
            multi: true,
        },
    ],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'CeCheckbox',
        '[class.CeCheckbox--small]': 'size() === "sm"',
        '[class.CeCheckbox--checked]': 'checked',
        '[class.CeCheckbox--disabled]': 'disabled',
        '[class.CeCheckbox--error]': 'hasError',
        '[class.CeCheckbox--read-only]': 'readOnly()',
    },
    templateUrl: './ce-checkbox.component.html',
    styleUrl: './ce-checkbox.component.scss',
})
export class CeCheckboxComponent implements ControlValueAccessor {
    @Input({ required: true }) label = '';
    @Input() id?: string = '';
    @Input() disabled?: boolean = false;
    @Input() hasError?: boolean = false;
    @Input() mixed?: boolean = false; // Used only for checkboxes inside tables
    readOnly = input<boolean>(false);
    size = input<'sm' | 'md'>('md');
    suffixIcon = input<keyof Icons | undefined>(undefined); // Used only for checkboxes inside dropdowns

    @ViewChild(TemplateRef) templateRef!: TemplateRef<CeCheckboxComponent>;

    @Input({ transform: booleanAttribute })
    get checked(): boolean {
        return this.#checked;
    }

    set checked(value: boolean) {
        if (value != this.checked) {
            this.#checked = value;
        }
    }

    #checked = false;
    checkboxId: string = uuid();

    value!: boolean;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChange: (value: boolean) => void = (): void => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onTouch: () => void = (): void => {};

    @Output() readonly checkboxValueChanged: EventEmitter<CeCheckboxChange> = new EventEmitter<CeCheckboxChange>();

    @ContentChild(CeErrorMessageDirective) errorMessage!: CeErrorMessageDirective;
    @ContentChild(CeCheckboxHelperTextDirective) helperText!: CeCheckboxHelperTextDirective;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    #controlValueAccessorChangeFn: (value: boolean) => void = (): void => {};

    protected _createChangeEvent(isChecked: boolean): CeCheckboxChange {
        const event = new CeCheckboxChange();
        event.source = this;
        event.checked = isChecked;
        return event;
    }

    #emitChangeEvent(): void {
        this.#controlValueAccessorChangeFn(this.checked);
        this.checkboxValueChanged.emit(this._createChangeEvent(this.checked));
    }

    onCheckboxClick(): void {
        this._handleInputClick();
    }

    protected _handleInputClick(): void {
        this.#checked = !this.#checked;
        this.#emitChangeEvent();
    }

    onCheckboxInteractionEvent(event: Event): void {
        // We always have to stop propagation on the change event.
        // Otherwise, the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
    }

    // Implemented as part of ControlValueAccessor.
    writeValue(value: boolean): void {
        this.checked = value;
        this.value = value;
    }

    // Implemented as part of ControlValueAccessor.
    registerOnChange(fn: (value: boolean) => void): void {
        this.#controlValueAccessorChangeFn = fn;
        this.onChange = fn;
    }

    // Implemented as part of ControlValueAccessor.
    registerOnTouched(fn: () => void): void {
        // to be implemented - needed for ControlValueAccessor
        this.onTouch = fn;
    }

    // Implemented as part of ControlValueAccessor.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}

export const CeCheckboxImports = [CeCheckboxComponent, CeCheckboxHelperTextDirective, CeErrorMessageDirective] as const;
